import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Calendar, Clock, MapPin, Users, Volleyball } from "lucide-react";

export default function GameCalendarSection({
  selectedDate, setSelectedDate, allGamedays, playerGamedays, calendarModifiers, calendarModifiersStyles, t,
}) {
  const selectedDateStr = selectedDate.toISOString().split("T")[0];
  const gamesOnDate = allGamedays.filter((gd) => (gd.date || gd.created_at || "").split("T")[0] === selectedDateStr);
  const playerGamesOnDate = playerGamedays.filter((gd) => (gd.date || gd.created_at || "").split("T")[0] === selectedDateStr);

  return (
    <Card className="bg-card/50 border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground text-lg">
          <Calendar className="h-5 w-5 text-[#CCFF00]" />
          {t("playerDashboard.gameCalendar") || "Calendario de Jogos"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex justify-center">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border border-border bg-muted/50"
              modifiers={calendarModifiers}
              modifiersStyles={calendarModifiersStyles}
            />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-muted-foreground mb-3">
              {selectedDate.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </h4>
            {gamesOnDate.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Volleyball className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{t("playerDashboard.noGameOnDate") || "Nenhum jogo nesta data"}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {gamesOnDate.map((gd, idx) => {
                  const isPlayerInGame = playerGamesOnDate.some((pg) => pg.id === gd.id);
                  return (
                    <div key={gd.id || idx} className={`p-3 rounded-lg border ${isPlayerInGame ? "bg-green-500/10 border-green-500/30" : "bg-muted/50 border-border/50"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-foreground">{t("playerDashboard.gameOfDay") || "Jogo do Dia"}</span>
                        {isPlayerInGame && <Badge className="bg-green-500/20 text-green-400 border-0 text-xs">{t("playerDashboard.youPlayed") || "Voce jogou!"}</Badge>}
                      </div>
                      {gd.location && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" />
                          {gd.location}
                        </div>
                      )}
                      {(gd.start_time || gd.end_time) && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Clock className="h-3.5 w-3.5" />
                          {gd.start_time || "--:--"} - {gd.end_time || "--:--"}
                        </div>
                      )}
                      {gd.generated_teams && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <Users className="h-3.5 w-3.5" />
                          {gd.generated_teams.length} {t("gameday.team")}s &bull; {gd.generated_teams.reduce((acc, tm) => acc + (tm.players?.length || 0), 0)} {t("nav.players")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-2">{t("playerDashboard.legend") || "Legenda"}:</p>
              <div className="flex flex-wrap gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#CCFF00]/20"></div>
                  <span className="text-muted-foreground">{t("playerDashboard.scheduledGame") || "Jogo agendado"}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-green-500/30 border-2 border-green-500"></div>
                  <span className="text-muted-foreground">{t("playerDashboard.youParticipated") || "Voce participou"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
